import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

export class SdpFormValidators {
  static uniqueAcross(controlName: string): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!(ctrl instanceof FormArray)) return null;

      for (const child of ctrl.controls) {
        if (!(child instanceof FormGroup)) continue;
        const field = child.get(controlName);
        if (!field) continue;
        const errs = field.errors || null;
        if (errs && 'duplicateInSiblings' in errs) {
          const { duplicateInSiblings, ...rest } = errs;
          field.setErrors(Object.keys(rest).length > 0 ? rest : null);
        }
      }

      const map = new Map<string, number[]>();
      ctrl.controls.forEach((child, idx) => {
        if (!(child instanceof FormGroup)) return;
        const c = child.get(controlName);
        if (!c) return;

        const val = c.value;

        if (val == null || val === '') return;
        if (!map.has(val)) map.set(val, []);

        map.get(val)!.push(idx);
      });

      let hasDup = false;
      for (const [, indices] of map) {
        if (indices.length > 1) {
          hasDup = true;
          for (const i of indices) {
            const group = ctrl.at(i);
            if (!(group instanceof FormGroup)) continue;
            const idCtrl = group.get(controlName);
            if (!idCtrl) continue;
            const errs = idCtrl.errors || {};
            idCtrl.setErrors({ ...errs, duplicateInSiblings: true });
            idCtrl.markAsTouched();
          }
        }
      }

      return hasDup ? { duplicateIds: true } : null;
    };
  }

  static overlappingDates(): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!(ctrl instanceof FormArray)) return null;

      // Clear previous errors
      ctrl.controls.forEach((child) => {
        for (const key of ['startDate', 'endDate']) {
          const c = child.get(key);
          if (c?.errors?.['overlappingDates']) {
            const { overlappingDates, ...rest } = c.errors;
            c.setErrors(Object.keys(rest).length ? rest : null);
          }
        }
      });

      let hasOverlap = false;

      // Compare every pair of date ranges
      for (let i = 0; i < ctrl.length; i++) {
        const a = ctrl.at(i);
        const aStart = a.get('startDate')?.value;
        const aEnd = a.get('endDate')?.value;

        if (!aStart) continue;

        for (let j = i + 1; j < ctrl.length; j++) {
          const b = ctrl.at(j);
          const bStart = b.get('startDate')?.value;
          const bEnd = b.get('endDate')?.value;

          if (!bStart) continue;

          if (SdpFormValidators.dateRangesOverlap(aStart, bStart, aEnd, bEnd)) {
            hasOverlap = true;

            // mark both controls
            for (const c of [a.get('startDate'), a.get('endDate'), b.get('startDate'), b.get('endDate')]) {
              if (!c) continue;
              const errs = c.errors || {};
              c.setErrors({ ...errs, overlappingDates: true });
              c.markAsTouched();
            }
          }
        }
      }

      return hasOverlap ? { overlappingDates: true } : null;
    };
  }

  private static dateRangesOverlap(aStart: Date, bStart: Date, aEnd?: Date, bEnd?: Date): boolean {
    const a0 = aStart.getTime();
    const a1 = aEnd ? aEnd.getTime() : Number.POSITIVE_INFINITY;
    const b0 = bStart.getTime();
    const b1 = bEnd ? bEnd.getTime() : Number.POSITIVE_INFINITY;

    if (a1 < a0 || b1 < b0) return false;

    return Math.max(a0, b0) <= Math.min(a1, b1);
  }
}
